import { useQuery, useQueryClient } from '@tanstack/react-query'

import { get } from '@/lib/api'

// ==================== Types ====================

export interface DashboardStatistics {
  activeProjects: {
    count: number
    change: number
  }
  weeklyHours: {
    total: number
    change: number
  }
  pendingApprovals: {
    count: number
    leaves: number
    reimbursements: number
  }
  monthlyCashFlow: {
    income: number
    expense: number
    net: number
    change: number
  } | null
}

export interface ProjectProgress {
  id: string
  name: string
  code: string
  progress: number
  dueDate: string | null
  isOverdue: boolean
  status: 'planning' | 'active' | 'completed'
  teamName: string | null
}

export interface TeamWorkload {
  employeeId: string
  employeeName: string
  teamName: string | null
  hoursThisWeek: number
  maxHours: number
}

export interface CashFlowTrend {
  month: string
  income: number
  expense: number
  net: number
}

export interface PendingApproval {
  id: string
  type: 'leave' | 'reimbursement'
  applicantId: string
  applicantName: string
  applicantDepartment: string | null
  leaveType?: string
  days?: number
  amount?: number
  category?: string
  submittedAt: string
  detailUrl: string
}

export interface AdminDashboardData {
  statistics: DashboardStatistics | null
  projects: ProjectProgress[] | null
  workload: TeamWorkload[] | null
  cashFlow: CashFlowTrend[] | null
  approvals: PendingApproval[] | null
}

export interface AdminDashboardErrors {
  statistics?: string
  projects?: string
  workload?: string
  cashFlow?: string
  approvals?: string
}

export interface AdminDashboardResponse {
  data: AdminDashboardData
  errors?: AdminDashboardErrors
}

// ==================== Query Keys ====================

export const adminDashboardKeys = {
  all: ['adminDashboard'] as const,
  dashboard: () => [...adminDashboardKeys.all, 'dashboard'] as const,
}

// ==================== Hooks ====================

/**
 * 获取管理仪表板数据
 * - 10分钟自动刷新
 * - 窗口聚焦时刷新
 */
export function useAdminDashboard() {
  return useQuery({
    queryKey: adminDashboardKeys.dashboard(),
    queryFn: () => get<AdminDashboardResponse>('/admin/dashboard'),
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true,
    refetchInterval: 10 * 60 * 1000, // 10 minutes auto refresh
  })
}

/**
 * 手动刷新仪表板数据
 */
export function useRefreshAdminDashboard() {
  const queryClient = useQueryClient()

  return () => {
    void queryClient.invalidateQueries({ queryKey: adminDashboardKeys.dashboard() })
  }
}
